import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { healthColor, PLATFORM_LABELS, formatINR } from "@/lib/helpers";
import { ArrowRight, Package, TrendUp, Warning, Sparkle, Storefront, CurrencyInr, ShieldCheck, Robot, Plugs, Coins, Lightning } from "@phosphor-icons/react";
import BuyCreditsModal from "@/components/BuyCreditsModal";

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [opps, setOpps] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [conns, setConns] = useState([]);
  const [payments, setPayments] = useState([]);
  const [credits, setCredits] = useState({ balance: null, loading: true, error: false });
  const [showBuy, setShowBuy] = useState(false);
  const nav = useNavigate();

  const loadCredits = () => {
    setCredits((c) => ({ ...c, loading: true, error: false }));
    api.get("/credits/balance")
      .then(r => setCredits({ balance: r.data.balance, loading: false, error: false }))
      .catch(() => setCredits({ balance: null, loading: false, error: true }));
  };

  useEffect(() => {
    api.get("/dashboard/summary").then(r => setSummary(r.data));
    api.get("/dashboard/opportunities").then(r => setOpps(r.data.items));
    api.get("/ai/jobs?limit=1").then(r => setJobs(r.data.items || [])).catch(()=>{});
    api.get("/platforms").then(r => setConns(r.data.items || [])).catch(()=>{});
    // Admin-only endpoint — avoid noisy 403 for non-superadmin users.
    if (user?.role === "superadmin") {
      api.get("/admin/stats").then(r => setPayments(r.data.payments || [])).catch(()=>{});
    }
    loadCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!summary) return <div className="font-mono-data text-sm text-muted-foreground">Loading command center…</div>;
  const latestJob = jobs[0];
  const shopify = conns.find(c => c.platform === "shopify");
  const freeCount = conns.filter(c => c.is_free_slot).length;
  const paidCount = conns.filter(c => !c.is_free_slot && c.is_active).length;
  const latestPay = payments.slice().sort((a,b)=>new Date(b.created_at)-new Date(a.created_at))[0];

  return (
    <div data-testid="dashboard-page" className="space-y-8 min-w-0">
      {/* Header */}
      <div className="min-w-0">
        <div className="eyebrow mb-2">EcomKit Command Center</div>
        <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tighter text-navy">
          {summary.total_products === 0
            ? "Start by importing products"
            : summary.actions.length ? "What needs your attention" : "Everything looks healthy"}
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          {summary.total_products === 0
            ? "Your catalog is empty. Use the Import Center or AI Builder to add your first products."
            : summary.actions.length
              ? `${summary.actions.length} item${summary.actions.length > 1 ? "s" : ""} across your catalog and marketplaces.`
              : "Your catalog is in great shape — nothing urgent right now."}
        </p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <Kpi label="Products" value={summary.total_products} icon={Package}/>
        <Kpi label="Live Listings" value={Object.values(summary.platform_live).reduce((a,b)=>a+b,0)} icon={ShieldCheck} tone="green"/>
        <Kpi label="Missing" value={Object.values(summary.platform_missing).reduce((a,b)=>a+b,0)} icon={Warning} tone="orange"/>
        <Kpi label="Incomplete" value={summary.incomplete} tone="orange"/>
        <Kpi label="Low Stock" value={summary.low_stock} tone="orange"/>
        <Kpi label="Out of Stock" value={summary.out_of_stock} tone="red"/>
        <Kpi label="Platforms" value={summary.connected_platforms.length} icon={Storefront}/>
        <Kpi label="Avg Health" value={summary.avg_health} suffix="/100" toneClass={healthColor(summary.avg_health)}/>
      </div>

      {/* Action + Health */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 min-w-0">
        <div className="lg:col-span-2 card-base p-6 min-w-0">
          <div className="flex items-center justify-between mb-5 gap-3">
            <div className="min-w-0">
              <div className="eyebrow">Today's Action Center</div>
              <div className="section-title mt-0.5">What needs your attention today?</div>
            </div>
            <Warning size={18} weight="fill" className="text-orange shrink-0"/>
          </div>
          <div className="space-y-1" data-testid="action-list">
            {summary.actions.length === 0 && (
              <div className="text-sm text-muted-foreground py-6 border border-dashed border-border rounded-lg text-center">
                No pending actions. Your catalog is healthy.
              </div>
            )}
            {summary.actions.map((a, i) => (
              <div key={i} className="flex items-center justify-between gap-3 border-b border-border py-3 last:border-0 min-w-0">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-1.5 h-1.5 bg-orange rounded-full shrink-0"/>
                  <span className="text-sm text-navy truncate">{a.message}</span>
                </div>
                <button onClick={() => nav(a.type === "missing" ? "/gap-finder" : "/products")}
                  className="text-xs font-semibold uppercase tracking-wider text-orange hover:underline flex items-center gap-1 shrink-0">
                  {a.action} <ArrowRight size={11}/>
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="card-base p-6 flex flex-col min-w-0">
          <div className="eyebrow">Catalog Health</div>
          {summary.total_products === 0 ? (
            <>
              <div className="mt-4 font-display text-6xl font-bold tabular-nums text-muted-foreground/40">—</div>
              <div className="text-sm text-muted-foreground font-mono-data">No products yet</div>
              <div className="mt-auto pt-4 text-xs text-muted-foreground">
                Import products to see catalog health.
              </div>
            </>
          ) : (
            <>
              <div className="mt-4 flex items-end gap-2">
                <div className={`font-display text-6xl font-bold tabular-nums ${healthColor(summary.avg_health)}`}>{summary.avg_health}</div>
                <div className="text-sm text-muted-foreground pb-2 font-mono-data">/ 100</div>
              </div>
              <div className="mt-3 h-2 bg-secondary rounded-full overflow-hidden">
                <div className="h-full bg-brand-green" style={{ width: `${summary.avg_health}%` }}/>
              </div>
              <div className="mt-auto pt-4 text-xs text-muted-foreground">
                {summary.incomplete} products need improvement to reach 90+.
              </div>
            </>
          )}
        </div>
      </div>

      {/* Platform matrix */}
      <div className="card-base p-6 min-w-0">
        <div className="flex items-center justify-between mb-5 gap-3 min-w-0">
          <div className="min-w-0">
            <div className="eyebrow">Product Availability</div>
            <div className="section-title mt-0.5">Where are your products?</div>
          </div>
          <button onClick={()=>nav("/matrix")} className="btn-ghost text-orange shrink-0">Open matrix →</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(summary.platform_live).map(([p, live]) => {
            const connected = summary.connected_platforms.includes(p);
            return (
              <div key={p} className={`border rounded-lg p-4 min-w-0 ${connected ? "bg-white border-border" : "bg-secondary/50 border-border"}`}>
                <div className="flex items-center justify-between mb-3 gap-2 min-w-0">
                  <div className="font-display font-semibold text-navy truncate">{PLATFORM_LABELS[p]}</div>
                  {connected ? <span className="pill pill-live shrink-0">Connected</span> : <span className="pill pill-neutral shrink-0">Off</span>}
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Live</div>
                    <div className="font-mono-data font-bold text-brand-green text-lg tabular-nums">{live}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Missing</div>
                    <div className="font-mono-data font-bold text-lg tabular-nums" style={{color:"hsl(var(--ecom-orange))"}}>{summary.platform_missing[p]}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Ops widgets */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 min-w-0">
        <div className="card-base p-6 min-w-0" data-testid="credit-widget">
          <div className="flex items-center justify-between mb-3 gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Coins size={16} weight="duotone" className="text-orange shrink-0"/>
              <div className="eyebrow">Gap Finder Credits</div>
            </div>
            <button onClick={() => setShowBuy(true)} data-testid="dashboard-buy-credits" className="text-xs text-orange font-semibold uppercase tracking-wider shrink-0 hover:underline">Buy →</button>
          </div>
          {credits.loading ? (
            <div className="font-mono-data text-sm text-muted-foreground">Loading…</div>
          ) : credits.error ? (
            <div>
              <div className="font-display text-2xl font-bold text-red-600">—</div>
              <button onClick={loadCredits} className="text-xs text-orange underline mt-1">Retry</button>
            </div>
          ) : (
            <div>
              <div className="font-display text-3xl font-bold text-navy tabular-nums" data-testid="dashboard-credit-balance">
                {credits.balance}
              </div>
              <div className="mt-2 text-xs text-muted-foreground font-mono-data">
                1 credit = 1 successful Gap Finder run
              </div>
              {credits.balance === 0 && (
                <button onClick={() => setShowBuy(true)} className="btn-primary mt-3 py-1.5 px-3 text-[11px]">
                  <Lightning size={12} weight="fill"/> Buy Credits
                </button>
              )}
            </div>
          )}
        </div>

        <div className="card-base p-6 min-w-0">
          <div className="flex items-center justify-between mb-3 gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Plugs size={16} weight="duotone" className="text-orange shrink-0"/>
              <div className="eyebrow">Platform Access</div>
            </div>
            <button onClick={()=>nav("/platforms")} className="text-xs text-orange font-semibold uppercase tracking-wider shrink-0">Manage →</button>
          </div>
          <div className="font-display text-2xl font-bold text-navy">{freeCount} Free · {paidCount} Paid</div>
          {shopify && (
            <div className="mt-3 text-xs text-muted-foreground">
              Shopify: {shopify.is_demo ? <span className="pill pill-neutral">Demo</span> : <span className="pill pill-live">Connected</span>}
              {shopify.last_synced_at && <span className="ml-2 font-mono-data">Last sync: {new Date(shopify.last_synced_at).toLocaleDateString()}</span>}
            </div>
          )}
        </div>

        <div className="card-base p-6 min-w-0">
          <div className="flex items-center justify-between mb-3 gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Robot size={16} weight="duotone" className="text-orange shrink-0"/>
              <div className="eyebrow">AI Jobs</div>
            </div>
            <button onClick={()=>nav("/jobs")} className="text-xs text-orange font-semibold uppercase tracking-wider shrink-0">All →</button>
          </div>
          {latestJob ? (
            <div>
              <div className="font-display text-2xl font-bold text-navy">{latestJob.completed}/{latestJob.total} completed</div>
              <div className="mt-2 h-1.5 bg-secondary rounded-full overflow-hidden">
                <div className="h-full bg-brand-green" style={{width: `${(latestJob.completed / (latestJob.total || 1)) * 100}%`}}/>
              </div>
              <div className="mt-2 text-xs text-muted-foreground font-mono-data">{latestJob.status.toUpperCase()} · {new Date(latestJob.created_at).toLocaleString()}</div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No jobs yet. Select products → AI Complete.</div>
          )}
        </div>

        <div className="card-base p-6 min-w-0">
          <div className="flex items-center justify-between mb-3 gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <CurrencyInr size={16} weight="duotone" className="text-orange shrink-0"/>
              <div className="eyebrow">Latest Payment</div>
            </div>
            <button onClick={()=>nav("/admin")} className="text-xs text-orange font-semibold uppercase tracking-wider shrink-0">History →</button>
          </div>
          {latestPay ? (
            <div>
              <div className="font-display text-2xl font-bold text-navy">{formatINR(latestPay.amount)}</div>
              <div className="mt-2 text-xs text-muted-foreground font-mono-data">
                {(latestPay.platform || latestPay.purpose || "").toString().toUpperCase()} · {latestPay.days ? `${latestPay.days}d · ` : ""}<span className={`pill ${latestPay.status==="paid_live"?"pill-live":"pill-draft"}`}>{latestPay.status}</span>
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No paid activations yet.</div>
          )}
        </div>
      </div>

      <BuyCreditsModal
        open={showBuy}
        onClose={() => setShowBuy(false)}
        onPurchased={() => { loadCredits(); setShowBuy(false); }}
      />

      {/* Opportunities */}
      <div className="card-base p-6 min-w-0">
        <div className="flex items-center gap-2 mb-5 min-w-0">
          <TrendUp size={18} weight="fill" className="text-brand-green shrink-0"/>
          <div className="min-w-0">
            <div className="eyebrow">Growth Opportunities</div>
            <div className="section-title mt-0.5">Small wins compound quickly</div>
          </div>
        </div>
        <div className="space-y-2" data-testid="opportunities">
          {opps.length === 0 && (
            <div className="text-sm text-muted-foreground py-6 border border-dashed border-border rounded-lg text-center">
              Great job! Nothing to improve right now.
            </div>
          )}
          {opps.map((o, i) => (
            <div key={i} className="flex items-center justify-between gap-3 border border-border rounded-lg p-4 hover:border-navy/40 transition-colors min-w-0">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-orange/10 flex items-center justify-center shrink-0">
                  <Sparkle size={18} weight="fill" className="text-orange"/>
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-navy truncate">{o.message}</div>
                  <div className="text-[11px] text-muted-foreground font-mono-data uppercase tracking-wider">{o.type.replace(/_/g," ")}</div>
                </div>
              </div>
              <button onClick={() => nav("/products")} className="btn-secondary shrink-0">
                {o.action}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, suffix, icon: Icon, tone, toneClass }) {
  const c = toneClass || (tone === "orange" ? "text-orange" : tone === "green" ? "text-brand-green" : tone === "red" ? "text-red-600" : "text-navy");
  return (
    <div className="kpi-card">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="kpi-label truncate">{label}</div>
          <div className={`kpi-value ${c}`}>{value}{suffix && <span className="text-sm text-muted-foreground font-normal ml-0.5">{suffix}</span>}</div>
        </div>
        {Icon && <Icon size={16} className="text-muted-foreground shrink-0 mt-1"/>}
      </div>
    </div>
  );
}
